import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import {Avatar, Box, Typography} from '@mui/material';

import {getCandidateInitials} from '../utils/getCandidateInitials';

import {TALENT_POOL_STATUS_IDS, TalentPoolListItem} from './types/TalentPoolTypes';
import {TableRowRequired} from '../../../../components/BasicTable/BasicTable';
import {BasicTableCell, BasicTableRow} from '../../../../components/BasicTable/BasicTableRow';
import {EmpionButton} from '../../../../components/EmpionButton/EmpionButton';
import {TextOverflowWithTooltip} from '../../../../components/TextOverflowWithTooltip/TextOverflowWithTooltip';
import {greenColors, orangeColors} from '../../../../layouts/theme/designSystem';
import {AppConfig} from '../../../../lib/AppConfig';
import {MATCH_DETAILS_FROM_TALENT_POOL, MATCH_DETAILS_RETURN_TO_PARAM} from '../../../../lib/matchDetailsNav';
import {transformDate} from '../../../../utils/dateUtils/dateFormattingUtils';

/** Dot + total-match bar use the same accent per status (Figma). */
const TALENT_POOL_STATUS_VISUAL: Record<string, {dot: string; barFill: string}> = {
  pool: {dot: orangeColors[70], barFill: orangeColors[70]},
  waiting_cv: {dot: orangeColors[40], barFill: orangeColors[40]},
  match: {dot: greenColors[700], barFill: greenColors[100]},
};

const statusVisualFallback = {dot: greenColors[70], barFill: greenColors[100]};

const MatchTotalBar = ({score, status}: {score: number | null | undefined; status: string}) => {
  const pct = score === null || score === undefined ? 0 : Math.min(100, Math.max(0, score));
  const {barFill} = TALENT_POOL_STATUS_VISUAL[status] ?? statusVisualFallback;

  return (
    <Box
      sx={{
        width: 52,
        height: 6,
        bgcolor: orangeColors[5],
        borderRadius: '2px',
        position: 'relative',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: `${pct}%`,
          bgcolor: barFill,
          borderRadius: '2px',
        }}
      />
    </Box>
  );
};

export const TalentPoolListRow = (row: TalentPoolListItem & TableRowRequired) => {
  const {t: tStatuses} = useTranslation('translation', {keyPrefix: 'talentPoolPage.statuses'});
  const {t: tPool} = useTranslation('translation', {keyPrefix: 'talentPoolPage'});
  const navigate = useNavigate();
  const {search: listSearch} = useLocation();

  const redirect = () => {
    const raw = listSearch.startsWith('?') ? listSearch.slice(1) : listSearch;
    const qs = new URLSearchParams(raw);
    qs.set(MATCH_DETAILS_RETURN_TO_PARAM, MATCH_DETAILS_FROM_TALENT_POOL);
    if (row.matchResourceId) {
      navigate(`/company/recruiting/match/${row.matchResourceId}?${qs.toString()}`);
      return;
    }
    const profileId = row.applicantResourceId ?? row.resourceId;
    navigate(`${AppConfig.COMPANY_ADMIN_RECRUITING_TALENT_POOL_PROFILE_PATH}/${profileId}?${qs.toString()}`);
  };

  const formatScore = (score: number | null | undefined) => {
    if (score === null || score === undefined) return '--%';
    return `${score}%`;
  };

  const overall = row.totalMatch;
  const {dot: dotColor} = TALENT_POOL_STATUS_VISUAL[row.status] ?? statusVisualFallback;
  const statusLabel = TALENT_POOL_STATUS_IDS.has(row.status)
    ? String(tStatuses(row.status as 'pool' | 'waiting_cv' | 'match'))
    : row.status.replace(/_/g, ' ');

  const initials = getCandidateInitials({fullName: row.candidateName});
  const actionLabel = tPool('match');
  const actionVariant = row.matchResourceId ? 'secondary' : 'primary';
  const actionIcon = <PersonOutlinedIcon sx={{fontSize: '16px'}} />;

  return (
    <BasicTableRow rowVariant='flat'>
      <BasicTableCell>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
          <Avatar
            alt=''
            sx={{
              width: 27,
              height: 27,
              fontSize: '10px',
              fontWeight: 700,
              bgcolor: orangeColors[5],
              color: greenColors[100],
            }}
          >
            {initials}
          </Avatar>
          <TextOverflowWithTooltip
            variant='body2'
            sxTypography={{fontSize: '13px', letterSpacing: '0.39px'}}
            text={row.candidateName}
          />
        </Box>
      </BasicTableCell>
      <BasicTableCell>
        <TextOverflowWithTooltip
          variant='body2'
          sxTypography={{fontSize: '13px', letterSpacing: '0.39px'}}
          text={row.campaignName ?? '—'}
        />
      </BasicTableCell>
      <BasicTableCell>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0}}>
          <Box sx={{width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0}} />
          <TextOverflowWithTooltip
            variant='body2'
            text={statusLabel}
            sx={{minWidth: 0}}
            sxTypography={{fontSize: '13px', lineHeight: '16px', letterSpacing: '0.39px', textTransform: 'capitalize'}}
          />
        </Box>
      </BasicTableCell>
      <BasicTableCell>
        <TextOverflowWithTooltip
          sxTypography={{fontSize: '13px', letterSpacing: '0.39px'}}
          variant='body2'
          text={row.date ? transformDate(row.date, 'numeric') : '-'}
        />
      </BasicTableCell>
      <BasicTableCell>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <MatchTotalBar score={overall} status={row.status} />
          <Typography sx={{fontSize: '13px', letterSpacing: '0.39px', textTransform: 'capitalize', minWidth: 36}}>
            {formatScore(overall)}
          </Typography>
        </Box>
      </BasicTableCell>
      <BasicTableCell align='center'>
        <Typography sx={{fontSize: '13px', letterSpacing: '0.39px', textTransform: 'capitalize'}}>
          {formatScore(row.jobMatch)}
        </Typography>
      </BasicTableCell>
      <BasicTableCell align='center'>
        <Typography sx={{fontSize: '13px', letterSpacing: '0.39px', textTransform: 'capitalize'}}>
          {formatScore(row.testMatch)}
        </Typography>
      </BasicTableCell>
      <BasicTableCell align='center'>
        <Typography sx={{fontSize: '13px', letterSpacing: '0.39px', textTransform: 'capitalize'}}>
          {formatScore(row.cultureMatch)}
        </Typography>
      </BasicTableCell>
      <BasicTableCell align='right' sx={{width: '13%', minWidth: 120}}>
        <EmpionButton
          data-tour={row.matchResourceId ? 'talentPool.rowAction.view' : 'talentPool.rowAction.match'}
          size='small'
          variant={actionVariant}
          disableElevation
          onClick={redirect}
          aria-label={String(tPool('viewApplicant'))}
          sx={{
            width: 104,
            minWidth: 104,
            height: 32,
            px: 1,
            borderRadius: '5px',
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: '16px',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            '& .MuiButton-startIcon': {
              marginLeft: 0,
              marginRight: '4px',
            },
          }}
          startIcon={actionIcon}
        >
          {actionLabel}
        </EmpionButton>
      </BasicTableCell>
    </BasicTableRow>
  );
};
